import axios from 'axios';

import type {
  FundAccountEngineConfigInfo,
  FundAccountRuleCreateParams,
  FundAccountRuleInfo,
  FundAccountRuleUpdateParams,
  UpdateFundAccountEngineConfigParams,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

/**
 * Mutating calls for the Fund Accounts config screen. Reads go through `useDataFetcher`; these are
 * the writes. Each wraps the error in `reportAxiosError` (toast) and rethrows so the caller can
 * keep the dialog open, revert an optimistic reorder, and reset its submitting flag.
 *
 * The `/sysconfig/api/v1` prefix — not the `/api/v1` the Guardrails sibling uses — is what the
 * service registry mounts a module's `get_sysconfig_blueprint` under. Verified against the
 * generated `openapi.json`, which is emitted from the registered app.
 */
export const fundAccountsBaseUrl = (organizationId: number): string =>
  `/sysconfig/api/v1/organizations/${organizationId}/fund_accounts`;

const rulesUrl = (organizationId: number) => `${fundAccountsBaseUrl(organizationId)}/rules`;

export const engineConfigUrl = (organizationId: number): string =>
  `${fundAccountsBaseUrl(organizationId)}/engine_config`;

export const createFundAccountRule = async (
  organizationId: number,
  values: FundAccountRuleCreateParams
): Promise<FundAccountRuleInfo> => {
  try {
    const response = await axios.post<FundAccountRuleInfo>(rulesUrl(organizationId), values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const updateFundAccountRule = async (
  organizationId: number,
  ruleId: number,
  values: FundAccountRuleUpdateParams
): Promise<FundAccountRuleInfo> => {
  try {
    const response = await axios.put<FundAccountRuleInfo>(`${rulesUrl(organizationId)}/${ruleId}`, values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

export const deleteFundAccountRule = async (organizationId: number, ruleId: number): Promise<void> => {
  try {
    await axios.delete(`${rulesUrl(organizationId)}/${ruleId}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** Two bodiless POST routes rather than one PATCH with a flag — that is the shape the BE exposes
 *  (`…/{rule_id}/enable` and `…/{rule_id}/disable`), and it differs from the guardrails sibling's
 *  `PATCH …/enable {is_enabled}`. */
export const setFundAccountRuleEnabled = async (
  organizationId: number,
  ruleId: number,
  isEnabled: boolean
): Promise<void> => {
  try {
    await axios.post(`${rulesUrl(organizationId)}/${ruleId}/${isEnabled ? 'enable' : 'disable'}`);
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** The body is the rule set's whole target order, not a delta: the BE requires an exact
 *  permutation of every live fund-account rule in the organization and fails the whole
 *  transaction with a 400 otherwise. Callers must build `orderedIds` from the unfiltered list. */
export const reorderFundAccountRules = async (organizationId: number, orderedIds: number[]): Promise<void> => {
  try {
    await axios.post(`${rulesUrl(organizationId)}/reorder`, { ordered_ids: orderedIds });
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};

/** A full replacement of both fields — an omitted `default_account_id` clears it. */
export const updateFundAccountEngineConfig = async (
  organizationId: number,
  values: UpdateFundAccountEngineConfigParams
): Promise<FundAccountEngineConfigInfo> => {
  try {
    const response = await axios.put<FundAccountEngineConfigInfo>(engineConfigUrl(organizationId), values);
    return response.data;
  } catch (error) {
    await reportAxiosError(error);
    throw error;
  }
};
